import type {
    DropdownMenuCheckboxItemProps,
    DropdownMenuContentProps,
    DropdownMenuItemProps,
    DropdownMenuProps,
    DropdownMenuRadioGroupProps,
    DropdownMenuRadioItemProps,
    DropdownMenuSubContentProps,
    DropdownMenuSubProps,
    DropdownMenuSubTriggerProps,
    DropdownMenuTriggerProps,
} from '@radix-ui/react-dropdown-menu';
import {
    CheckboxItem,
    Content,
    Group,
    Item,
    ItemIndicator,
    Label,
    Portal,
    RadioGroup,
    RadioItem,
    Root,
    Separator,
    Sub,
    SubContent,
    SubTrigger,
    Trigger,
} from '@radix-ui/react-dropdown-menu';
import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';

import { useColorSchemeScope } from '../color-scheme/ColorSchemeScope';
import { useRestoreMenuFocusWithoutScroll } from '../focus/restore-menu-trigger-focus';
import { CheckIcon } from '../icons/CheckIcon';
import { ShortcutTooltip, type ShortcutTooltipProps } from '../shortcut-tooltip/ShortcutTooltip';
import { floatingSurfaceClassNames, floatingSurfaceItemClassNames } from '../surface/surfaceClasses';
import { Tooltip } from '../tooltip/Tooltip';

// biome-ignore lint/complexity/noBannedTypes: suppressed due to migration
export type DropdownMenuSeparatorProps = {};

export type DropdownMenuLabelProps = {
    readonly children: ReactNode;
};

export type DropdownMenuGroupProps = {
    readonly children: ReactNode;
};

const contentClasses = clsx(
    floatingSurfaceClassNames.tone.gray,
    'font-sans rounded-xl min-w-[220px] max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-x-hidden overflow-y-auto p-xs z-[1205]',
);

// Checkbox, radio and submenu rows follow the pointer through Radix's highlight rather than :hover, so a row the pointer
// crosses on its way into an open submenu stays plain while Radix keeps that submenu's row highlighted.
const baseItemClasses = clsx(
    'flex min-w-0 items-center rounded-sm text-text-form cursor-pointer',
    'text-body-xxs font-normal',
    'py-s px-m relative select-none',
    'data-[disabled]:text-text-quiet data-[disabled]:cursor-default data-[disabled]:pointer-events-none',
);

const highlightedClasses = floatingSurfaceItemClassNames.highlighted;

const checkedClasses = floatingSurfaceItemClassNames.checked;

export const DropdownMenu: FunctionComponent<DropdownMenuProps> = ({ children, onOpenChange, open, ...props }) => {
    return (
        <Root open={open} onOpenChange={onOpenChange} {...props}>
            {children}
        </Root>
    );
};

export const DropdownMenuTrigger: FunctionComponent<
    PropsWithChildren<
        DropdownMenuTriggerProps & {
            readonly shortcutTooltip?: Omit<ShortcutTooltipProps, 'children'>;
        }
    >
> = ({ children, shortcutTooltip, ...props }) => {
    const trigger = (
        <Trigger asChild={true} {...props}>
            {children}
        </Trigger>
    );

    return shortcutTooltip === undefined ? trigger : <ShortcutTooltip {...shortcutTooltip}>{trigger}</ShortcutTooltip>;
};

export const DropdownMenuItem: FunctionComponent<DropdownMenuItemProps & { readonly tooltipContent?: string }> = ({
    children,
    className,
    disabled,
    onSelect,
    tooltipContent,
    ...props
}) => {
    const menuItem = (
        <Item
            className={clsx(
                baseItemClasses,
                floatingSurfaceItemClassNames.hover,
                'gap-xs',
                '[&_svg]:text-text-content-faint',
                floatingSurfaceItemClassNames.highlightedSelected,
                'data-[disabled]:[&_svg]:text-text-quiet',
                className,
            )}
            disabled={disabled}
            onSelect={onSelect}
            {...props}
        >
            {children}
        </Item>
    );

    if (disabled === true && is.nonEmptyString(tooltipContent)) {
        return (
            <Tooltip>
                <Tooltip.Trigger asChild={true}>
                    <div style={{ cursor: 'not-allowed', display: 'flex' }}>{menuItem}</div>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content side="right" sideOffset={5}>
                        {tooltipContent}
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip>
        );
    }

    return menuItem;
};

export const DropdownMenuCheckboxItem: FunctionComponent<DropdownMenuCheckboxItemProps> = ({
    checked,
    children,
    className,
    disabled,
    onCheckedChange,
    ...props
}) => {
    return (
        <CheckboxItem
            checked={checked}
            className={clsx(baseItemClasses, highlightedClasses, checkedClasses, 'gap-s', className)}
            disabled={disabled}
            onCheckedChange={onCheckedChange}
            {...props}
        >
            <span className="min-w-0 flex-1 truncate">{children}</span>
            <ItemIndicator
                className="ml-s inline-flex size-4 shrink-0 items-center justify-center text-accent"
                data-testid="menu-item-indicator"
            >
                <CheckIcon />
            </ItemIndicator>
        </CheckboxItem>
    );
};

export const DropdownMenuSeparator: FunctionComponent<DropdownMenuSeparatorProps> = ({ ...props }) => {
    return <Separator className="bg-border-subtle h-px m-xs" {...props} />;
};

export const DropdownMenuLabel: FunctionComponent<DropdownMenuLabelProps> = ({ children, ...props }) => {
    return (
        <Label className="text-text-content-subtle text-xxxs font-semibold uppercase tracking-[0.08em] p-s" {...props}>
            {children}
        </Label>
    );
};

export const DropdownMenuGroup: FunctionComponent<DropdownMenuGroupProps> = ({ children, ...props }) => {
    return (
        <Group
            className="data-[disabled]:text-text-quiet data-[disabled]:cursor-default data-[disabled]:pointer-events-none"
            {...props}
        >
            {children}
        </Group>
    );
};

export const DropdownMenuSub: FunctionComponent<DropdownMenuSubProps> = ({
    children,
    onOpenChange,
    open,
    ...props
}) => {
    return (
        <Sub open={open} onOpenChange={onOpenChange} {...props}>
            {children}
        </Sub>
    );
};

export const DropdownMenuRadioGroup: FunctionComponent<DropdownMenuRadioGroupProps> = ({ children, ...props }) => {
    return (
        <RadioGroup
            className="data-[disabled]:text-text-quiet data-[disabled]:cursor-default data-[disabled]:pointer-events-none"
            {...props}
        >
            {children}
        </RadioGroup>
    );
};

export const DropdownMenuRadioItemContent: FunctionComponent<PropsWithChildren<{ readonly className?: string }>> = ({
    children,
    className,
}) => {
    return <div className={clsx('min-w-0 flex-1 truncate', className)}>{children}</div>;
};

export const DropdownMenuRadioItem: FunctionComponent<DropdownMenuRadioItemProps> = ({
    children,
    className,
    ...props
}) => {
    return (
        <RadioItem className={clsx(baseItemClasses, highlightedClasses, checkedClasses, className)} {...props}>
            {children}
        </RadioItem>
    );
};

export const DropdownMenuSubTrigger: FunctionComponent<DropdownMenuSubTriggerProps> = ({
    children,
    className,
    ...props
}) => {
    return (
        <SubTrigger
            className={clsx(
                baseItemClasses,
                highlightedClasses,
                'justify-between',
                '[&_svg]:text-text-content-faint',
                'data-[state=open]:bg-surface-subtle',
                'data-[disabled]:[&_svg]:text-text-quiet',
                className,
            )}
            {...props}
        >
            {children}
        </SubTrigger>
    );
};

export const DropdownMenuSubContent: FunctionComponent<DropdownMenuSubContentProps> = ({
    alignOffset,
    children,
    className,
    collisionPadding = 8,
    sideOffset,
    ...props
}) => {
    const colorScheme = useColorSchemeScope();

    return (
        <Portal>
            <SubContent
                alignOffset={alignOffset}
                className={clsx(contentClasses, className)}
                collisionPadding={collisionPadding}
                data-color-scheme={colorScheme}
                sideOffset={sideOffset}
                {...props}
            >
                {children}
            </SubContent>
        </Portal>
    );
};

export const DropdownMenuContent: FunctionComponent<
    DropdownMenuContentProps & {
        // Radix omits this from the dropdown content type although the menu content accepts it.
        readonly onOpenAutoFocus?: (event: Event) => void;
        readonly portalContainer?: HTMLElement | null;
    }
> = ({
    children,
    className,
    collisionPadding = 8,
    onCloseAutoFocus,
    onInteractOutside,
    onOpenAutoFocus,
    portalContainer,
    ...props
}) => {
    const colorScheme = useColorSchemeScope();
    const focusHandlers = useRestoreMenuFocusWithoutScroll({ onCloseAutoFocus, onInteractOutside, onOpenAutoFocus });

    return (
        <Portal container={portalContainer ?? undefined}>
            <Content
                className={clsx(contentClasses, className)}
                collisionPadding={collisionPadding}
                data-color-scheme={colorScheme}
                {...focusHandlers}
                {...props}
            >
                {children}
            </Content>
        </Portal>
    );
};

export const DropdownMenuItemIndicator: FunctionComponent<PropsWithChildren> = ({ children }) => {
    return (
        <ItemIndicator
            className="inline-flex shrink-0 items-center justify-center text-text-form ml-s"
            data-testid="menu-item-indicator"
        >
            {children}
        </ItemIndicator>
    );
};

export const DropdownMenuSubTriggerContent: FunctionComponent<PropsWithChildren> = ({ children }) => {
    return <div className="flex min-w-0 flex-1 items-center gap-xs [&>span]:min-w-0 [&>span]:truncate">{children}</div>;
};
